import styled, { css } from 'styled-components';
import { UiColors } from 'assets/styles/old/global';
import tagPalette from './tagPalette';
import { focusRing } from './a11y';

export const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

/* A single assigned-tag chip. Fill/text come from tagPalette: mango for custom tags, forest for
   form-linked ones. */
export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 3px 8px;
  border-radius: 999px;
  ${tagPalette}
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/* The little "x" that removes a tag from the file. Inherits the chip's text colour.

   Wrapped in `&&` — on the visit edit form these buttons live inside SurveyJS' markup, where
   `.sv_main button` (survey.css: teal fill, bold, min-width 100px, 2em padding, square corners)
   outranks a plain styled-components class. Doubling the class wins that back, so the chips look the
   same here as on the media pages. */
export const Remove = styled.button`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    padding: 0;
    border: none;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
    opacity: 0.8;

    &:hover {
      opacity: 1;
      background: transparent;
    }

    ${focusRing}

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

/* "+N" overflow chip (only rendered with `maxVisible`) — click reveals the hidden chips in place.
   Solid-bordered so it reads as a counter rather than the dashed "+ Tag" adder next to it. */
export const MoreButton = styled.button`
  && {
    display: inline-flex;
    align-items: center;
    min-width: 0;
    padding: 3px 8px;
    border-radius: 999px;
    border: 1px solid ${UiColors.strokeColor};
    background: ${UiColors.whiteSmokeFillColor};
    color: ${UiColors.dimGreyFillColor};
    font-size: 11px;
    font-weight: 600;
    line-height: 1.3;
    cursor: pointer;

    &:hover {
      border-color: ${UiColors.forestColor};
      color: ${UiColors.forestColor};
      background: ${UiColors.whiteSmokeFillColor};
    }

    ${focusRing}
  }
`;

/* The dashed "+ Tag" trigger. `&&` for the same reason as Remove above. */
export const AddButton = styled.button`
  && {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    padding: 3px 10px;
    border-radius: 999px;
    border: 1px dashed ${UiColors.silverFillColor};
    background: transparent;
    color: ${UiColors.dimGreyFillColor};
    font-size: 11px;
    font-weight: 600;
    line-height: 1.3;
    cursor: pointer;

    &:hover {
      border-color: ${UiColors.forestColor};
      color: ${UiColors.forestColor};
      background: transparent;
    }

    ${focusRing}

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

/* Portaled add-tag popover (search + checkbox list), positioned via inline top/bottom/left. Portaled to
   <body>, so it's a sibling of any overlay rather than a child and needs its own layer to sit above page
   chrome. Deliberately BELOW the review modal (10050, review/MediaReviewModal.styles.js) — that modal has
   its own tag sidebar and no popover, so a stray "+ Tag" popover left open on a tile should be covered by
   it — and below the toast container (100000) so notifications still surface over everything. */
export const Panel = styled.div`
  position: fixed;
  z-index: 10000;
  width: 280px;
  max-width: calc(100vw - 16px);
  background: ${UiColors.fillWhiteColor};
  border: 1px solid ${UiColors.strokeColor};
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(13, 13, 13, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid ${UiColors.strokeColor};
  color: ${UiColors.dimGreyFillColor};

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  input {
    border: none;
    outline: none;
    width: 100%;
    font-size: 13px;
    color: ${UiColors.fontColor};

    &:focus-visible {
      outline: 2px solid var(--dodger-blue);
      outline-offset: 2px;
    }

    &::placeholder {
      color: ${UiColors.silverFillColor};
    }
  }
`;

export const List = styled.div`
  max-height: 220px;
  overflow-y: auto;
  padding: 4px;
`;

export const Option = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: ${UiColors.fontColor};

  &:hover {
    background: ${UiColors.whiteSmokeFillColor};
  }

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

export const Box = styled.span`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid ${UiColors.silverFillColor};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${UiColors.fillWhiteColor};

  svg {
    width: 12px;
    height: 12px;
  }

  ${({ checked }) =>
    checked &&
    css`
      background: ${UiColors.forestColor};
      border-color: ${UiColors.forestColor};
    `}
`;

export const EmptyText = styled.div`
  padding: 12px 8px;
  font-size: 12px;
  color: ${UiColors.dimGreyFillColor};
  font-style: italic;
`;
